import { useState, useEffect, useRef, useMemo } from 'react'
import {
  forecastApi,
  streamForecast,
  ForecastStatus,
  ForecastResult,
} from '../../utils/api'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { StatusBadge } from './ForecastBadges'
import { ProgressBar } from './ProgressBar'
import { agentIcons, POLL_INTERVAL_MS, ANALYSIS_FETCH_THRESHOLD, STAGE_FEED_MAX_ITEMS } from './constants'

interface ActiveForecastProps {
  forecastId: string
  onComplete: (result: ForecastResult) => void
}

interface StageFeedItem {
  ts: number
  stage: string
  progress: number
  status: ForecastStatus['status']
}

// Average forecast duration in seconds (calibrate based on real data)
const AVG_FORECAST_DURATION_S = 90

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return 'Almost done...'
  if (seconds < 60) return `~${Math.ceil(seconds)}s remaining`
  const mins = Math.floor(seconds / 60)
  const secs = Math.ceil(seconds % 60)
  return `~${mins}m ${secs}s remaining`
}

function useElapsedTime(startTime: number | null) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startTime) return

    const start = startTime
    const update = () => setElapsed(Math.floor((Date.now() - start) / 1000))
    update()

    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [startTime])

  return elapsed
}

export function ActiveForecast({ forecastId, onComplete }: ActiveForecastProps) {
  const [status, setStatus] = useState<ForecastStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stageFeed, setStageFeed] = useState<StageFeedItem[]>([])
  const [startTime, setStartTime] = useState<number | null>(null)

  const lastStageRef = useRef<string | null>(null)
  const pollRef = useRef<number | null>(null)
  const completedRef = useRef(false)
  const analysisFetchedRef = useRef(false)

  const { copy } = useCopyToClipboard()
  const elapsedSeconds = useElapsedTime(startTime)

  // Estimate remaining time based on progress
  const timeEstimate = useMemo(() => {
    if (!status || status.progress <= 0) return null

    // Use actual elapsed time to estimate remaining
    const progress = status.progress / 100
    if (progress >= 0.95) return 'Almost done...'

    // Estimate total time based on current pace, with some smoothing
    const estimatedTotal = elapsedSeconds / progress
    const remaining = estimatedTotal - elapsedSeconds

    // Clamp to reasonable bounds
    const clampedRemaining = Math.max(5, Math.min(remaining, AVG_FORECAST_DURATION_S * 2))
    return formatTimeRemaining(clampedRemaining)
  }, [status?.progress, elapsedSeconds])

  const pushStage = (
    nextStage: string | null,
    nextProgress: number,
    nextStatus: ForecastStatus['status']
  ) => {
    if (!nextStage) return
    const stageText = String(nextStage)
    if (stageText === lastStageRef.current) return
    lastStageRef.current = stageText
    setStageFeed((prev) =>
      [{ ts: Date.now(), stage: stageText, progress: nextProgress, status: nextStatus }, ...prev].slice(
        0,
        STAGE_FEED_MAX_ITEMS
      )
    )
  }

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const ensurePolling = (forecastIdForPoll: string) => {
    if (pollRef.current) return
    pollRef.current = window.setInterval(async () => {
      try {
        const latest = await forecastApi.status(forecastIdForPoll)
        setStatus(latest)
        pushStage(latest.stage, latest.progress, latest.status)

        if (latest.analysis) analysisFetchedRef.current = true

        if (latest.status === 'failed') {
          setError(latest.error || 'Unknown error')
          stopPolling()
        }
        if (latest.status === 'completed' && latest.result && !completedRef.current) {
          completedRef.current = true
          onComplete(latest.result)
          stopPolling()
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to fetch status')
      }
    }, POLL_INTERVAL_MS)
  }

  const copyLink = async () => {
    const url = new URL(window.location.href)
    url.searchParams.set('id', forecastId)
    await copy(url.toString(), 'Link')
  }

  useEffect(() => {
    // Reset per-run state
    completedRef.current = false
    analysisFetchedRef.current = false
    lastStageRef.current = null
    setStageFeed([])
    stopPolling()
    setError(null)
    setStatus(null)
    setStartTime(Date.now())

    let isCancelled = false
    if (!forecastId) return () => undefined

    const setMergedStatus = (partial: Partial<ForecastStatus>) => {
      setStatus((prev) => {
        const base: ForecastStatus = {
          forecast_id: forecastId,
          question: '',
          status: 'pending',
          stage: null,
          progress: 0,
          analysis: null,
          result: null,
          error: null,
          started_at: null,
          completed_at: null,
        }
        const next = { ...base, ...(prev || {}), ...partial }
        return next
      })
    }

    const refreshDetails = async () => {
      try {
        const latest = await forecastApi.status(forecastId)
        if (isCancelled) return
        setStatus(latest)
        pushStage(latest.stage, latest.progress, latest.status)
        if (latest.analysis) analysisFetchedRef.current = true
        if (latest.status === 'failed') {
          setError(latest.error || 'Unknown error')
        }
        if (latest.status === 'completed' && latest.result && !completedRef.current) {
          completedRef.current = true
          onComplete(latest.result)
        }
      } catch (e: any) {
        if (isCancelled) return
        setError(e?.message || 'Failed to fetch status')
      }
    }

    // Initial fetch for question + analysis (SSE progress events don't include these)
    refreshDetails()

    const cleanup = streamForecast(
      forecastId,
      (progress) => {
        if (isCancelled) return
        setMergedStatus({
          status: progress.status as ForecastStatus['status'],
          stage: progress.stage ?? null,
          progress: progress.progress ?? 0,
        })
        pushStage(
          progress.stage ?? null,
          progress.progress ?? 0,
          progress.status as ForecastStatus['status']
        )

        // Opportunistically fetch analysis once it should exist
        if (!analysisFetchedRef.current && (progress.progress ?? 0) >= ANALYSIS_FETCH_THRESHOLD) {
          analysisFetchedRef.current = true // prevent stampede
          refreshDetails()
        }
      },
      (result) => {
        if (isCancelled) return
        stopPolling()
        if (completedRef.current) return
        completedRef.current = true
        setMergedStatus({ status: 'completed', progress: 100, result })
        pushStage('system: Forecast complete', 100, 'completed')
        onComplete(result)
      },
      (err) => {
        if (isCancelled) return
        // If we get an explicit forecast error, show it. Otherwise, fall back to polling.
        setError(err)
        ensurePolling(forecastId)
      }
    )

    return () => {
      isCancelled = true
      cleanup()
      stopPolling()
    }
  }, [forecastId, onComplete])

  if (!status) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-signal-blue/30 border-t-signal-blue rounded-full animate-spin" />
      </div>
    )
  }

  const parseStage = (s: string) => {
    const idx = s.indexOf(':')
    if (idx === -1) return { agent: null, message: s }
    return { agent: s.slice(0, idx).trim().toLowerCase(), message: s.slice(idx + 1).trim() }
  }

  const iconForAgent = (agent: string | null) => {
    const a = (agent || '').toLowerCase()
    return agentIcons[a] || agentIcons.default
  }

  return (
    <div className="space-y-4 sm:space-y-6 overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge status={status.status} />
          <span className="text-xs text-star-dim font-mono truncate">#{status.forecast_id}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={copyLink}
            className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star hover:border-signal-blue/40 transition-all"
          >
            <span className="hidden sm:inline">Copy link</span>
            <span className="sm:hidden">Link</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden">
        <h4 className="text-sm font-mono text-star-dim mb-2">Question</h4>
        <p className="text-base sm:text-lg text-star [overflow-wrap:anywhere]">{status.question || '—'}</p>
      </div>

      <div className="space-y-2">
        <ProgressBar progress={status.progress} stage={status.stage} />
        <div className="flex items-center justify-between text-xs text-star-dim">
          <span className="font-mono">{elapsedSeconds > 0 ? `${elapsedSeconds}s elapsed` : 'Starting...'}</span>
          {timeEstimate && <span className="font-mono">{timeEstimate}</span>}
        </div>
      </div>

      {stageFeed.length > 0 && (
        <div className="p-3 sm:p-4 bg-void-50 rounded-lg space-y-3 overflow-hidden">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-mono text-star-dim">Live updates</h4>
            <span className="text-xs text-star-dim font-mono">{stageFeed.length}</span>
          </div>
          <div className="space-y-2">
            {stageFeed.map((evt) => {
              const parsed = parseStage(evt.stage)
              return (
                <div key={evt.ts} className="flex items-start gap-2 sm:gap-3 text-sm overflow-hidden">
                  <span className="text-base sm:text-lg leading-none mt-0.5 shrink-0">{iconForAgent(parsed.agent)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-star-muted text-xs sm:text-sm flex-1 min-w-0 [overflow-wrap:anywhere]">
                        {parsed.agent ? (
                          <>
                            <span className="font-mono text-star-dim">{parsed.agent}</span>
                            <span className="text-star-dim">:</span> {parsed.message}
                          </>
                        ) : (
                          parsed.message
                        )}
                      </p>
                      <span className="text-xs text-star-dim font-mono shrink-0">
                        {evt.progress}%
                      </span>
                    </div>
                    <div className="text-xs text-star-dim/70 font-mono">
                      {new Date(evt.ts).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {status.analysis && (
        <div className="p-3 sm:p-4 bg-void-50 rounded-lg space-y-3">
          <h4 className="text-sm font-mono text-star-dim">Analysis</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
            <div className="flex flex-wrap gap-1">
              <span className="text-star-dim">Category:</span>
              <span className="text-star">{status.analysis.category}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              <span className="text-star-dim">Type:</span>
              <span className="text-star">{status.analysis.question_type}</span>
            </div>
            {status.analysis.primary_entity && (
              <div className="flex flex-wrap gap-1">
                <span className="text-star-dim">Entity:</span>
                <span className="text-star break-all">{status.analysis.primary_entity}</span>
              </div>
            )}
            {status.analysis.target_date && (
              <div className="flex flex-wrap gap-1">
                <span className="text-star-dim">Target:</span>
                <span className="text-star">{status.analysis.target_date}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-signal-red/10 border border-signal-red/30 rounded-lg">
          <p className="text-signal-red text-sm">{error}</p>
        </div>
      )}
    </div>
  )
}
